import { keepPreviousData, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronRight, Search } from "lucide-react";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useSearch } from "wouter";
import type { SearchGroup, SearchHit, SearchResponse } from "../../shared/home";
import type { ToolMeta } from "../../shared/tools";
import { webTools } from "../../tools/web";
import { api } from "../lib/api";
import { useCompany } from "../lib/company";
import { useMoney } from "../lib/money";
import { SyncBadge } from "./SyncBadge";
import { useWithWindows } from "./ToolWindows";
import { Spinner, TONE_FILL } from "./ui";

/*
 * Search across every tool (each tool's home.ts search, via /api/c/:id/home/search), from anywhere: Home's search box, and a popup
 * over any page (SearchHost), opened from the sidebar or top bar (SearchButton), or with Ctrl+K (⌘K) or "/". A result with a window opens it
 * over the page you're on (keeping it and any other windows open); others go to their page.
 */

/** The tools this business has (their search, and what their results come from). */
export const companyTools = (ids: readonly string[]) => ids.map((id) => webTools[id]?.meta).filter((m): m is ToolMeta => m !== undefined);

/** What the search box says it searches. */
export const searchPlaceholder = (tools: readonly ToolMeta[]) => (tools.some((t) => t.id === "pricelist") ? "Search items, bundles, suppliers and groups" : "Search");

/** Waits for a pause in typing, so each keystroke doesn't ask the server. */
export function useSettled(value: string, ms = 200) {
    const [settled, setSettled] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setSettled(value), ms);
        return () => clearTimeout(timer);
    }, [value, ms]);
    return settled;
}

/** The results for a search (after a pause in typing), kept up to date with the data they come from. */
export function useSearchResults(query: string, tools: readonly ToolMeta[]) {
    const company = useCompany();
    const q = useSettled(query.trim());
    const objects = useMemo(() => [...new Set(tools.flatMap((t) => t.datasets ?? []))], [tools]);
    const result = useQuery({
        queryKey: ["c", company.id, "home", "search", q],
        queryFn: () => api<SearchResponse>(`/c/${company.id}/home/search?q=${encodeURIComponent(q)}`),
        enabled: q.length > 0,
        placeholderData: keepPreviousData,
        meta: { sm8: objects, alsoOn: tools.map((t) => t.id) },
    });
    return { ...result, q };
}

/** Where a result goes: its window over the page you're on (keeping the rest of the address: the page, other windows), else its page. */
export function useHitHref() {
    const [location] = useLocation();
    const search = useSearch();
    return (hit: SearchHit) => {
        if (!hit.opens) return hit.href;
        const params = new URLSearchParams(search);
        for (const [key, value] of new URLSearchParams(hit.opens)) params.set(key, value);
        return `${location}?${params}`;
    };
}

/** A result: its sync icon (where it lives), name, detail and price. `active`: picked with the arrow keys. */
export function SearchRow({ hit, href, first, active, onPick }: Readonly<{ hit: SearchHit; href: string; first?: boolean; active?: boolean; onPick?: () => void }>) {
    const fmt = useMoney();
    return (
        <li className="border-b border-line last:border-b-0">
            <Link
                href={href}
                data-first-hit={first || undefined}
                onClick={onPick}
                className={clsx("flex items-center gap-3 px-4 py-2.5 text-ink no-underline hover:bg-surface-2", active && "bg-brand-500/10")}
            >
                {hit.synced === null ? null : <SyncBadge synced={hit.synced} />}
                <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{hit.name}</span>
                    {hit.detail ? <span className="block truncate text-xs text-muted">{hit.detail}</span> : null}
                </span>
                {hit.price === undefined ? null : <span className="shrink-0 tabular-nums">{fmt.money(hit.price)}</span>}
                <ChevronRight className="size-4 shrink-0 text-muted" />
            </Link>
        </li>
    );
}

/** Under a kind's results, when there are more than shown: all of them, in its list, searched the same. */
export function SeeAll(props: Readonly<{ group: SearchGroup | undefined; shown: number; query: string; onPick?: () => void }>) {
    const withWindows = useWithWindows();
    const { group } = props;
    if (!group || group.total <= props.shown) return null;
    return (
        <Link href={withWindows(group.href)} onClick={props.onPick} className="block border-t border-line px-4 py-2 text-sm text-brand-ink no-underline hover:bg-surface-2">
            See all {group.total.toLocaleString()} {group.kind.toLowerCase()}s matching &ldquo;{props.query}&rdquo; &rarr;
        </Link>
    );
}

/** The results, by kind (each with See all when there are more), `active` the one Enter opens. */
export function SearchGroups(props: Readonly<{ data: SearchResponse | undefined; q: string; active?: number; onPick?: () => void }>) {
    const hrefOf = useHitHref();
    const hits = props.data?.hits ?? [];
    if (!hits.length) return <p className="m-0 p-6 text-center text-sm text-muted">{props.data ? `Nothing matches "${props.q}".` : "Searching…"}</p>;
    const kinds = [...new Set(hits.map((h) => h.kind))];
    return (
        <>
            {kinds.map((kind) => {
                const ofKind = hits.filter((h) => h.kind === kind);
                const tone = props.data?.groups.find((g) => g.kind === kind)?.tone;
                return (
                    <div key={kind}>
                        <div className="flex items-center gap-2 border-b border-line bg-surface-2 px-4 py-1.5 text-xs font-semibold tracking-wide text-muted uppercase">
                            {/* Its kind's colour, as its windows' title bars. */}
                            {tone ? <span className={clsx("size-2 rounded-full", TONE_FILL[tone])} /> : null}
                            {kind}s
                        </div>
                        <ul className="m-0 list-none p-0">
                            {ofKind.map((h) => (
                                <SearchRow key={h.id} hit={h} href={hrefOf(h)} first={h === hits[0]} active={props.active !== undefined && hits.indexOf(h) === props.active} onPick={props.onPick} />
                            ))}
                        </ul>
                        <SeeAll group={props.data?.groups.find((g) => g.kind === kind)} shown={ofKind.length} query={props.q} onPick={props.onPick} />
                    </div>
                );
            })}
        </>
    );
}

/** Ctrl+K or ⌘K, or "/" while not typing somewhere: open search. */
const opensSearch = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") return true;
    const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName));
    return e.key === "/" && !typing && !e.ctrlKey && !e.metaKey && !e.altKey;
};

/** The shortcut, as this computer has it ("⌘K" on a Mac, else "Ctrl K"). */
export const searchShortcut = typeof navigator !== "undefined" && /mac/i.test(navigator.platform) ? "⌘K" : "Ctrl K";

/** Opening search from anywhere (SearchHost), for its buttons. */
const SearchOpener = createContext<(() => void) | null>(null);

/**
 * Search from anywhere, once for the app (the Shell): its shortcuts, wherever the app has the keyboard, and its popup, over the page
 * and any windows (closed with Esc, a click outside, or opening a result). Its buttons (SearchButton) open it.
 */
export function SearchHost({ toolIds, children }: Readonly<{ toolIds: readonly string[]; children: ReactNode }>) {
    const tools = useMemo(() => companyTools(toolIds), [toolIds]);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!tools.length) return;
        const onKey = (e: KeyboardEvent) => {
            if (!opensSearch(e)) return;
            e.preventDefault();
            setOpen(true);
        };
        globalThis.addEventListener("keydown", onKey);
        return () => globalThis.removeEventListener("keydown", onKey);
    }, [tools.length]);
    return (
        <SearchOpener value={tools.length ? () => setOpen(true) : null}>
            {children}
            {open ? <SearchPopup tools={tools} onClose={() => setOpen(false)} /> : null}
        </SearchOpener>
    );
}

/** The button that opens search (SearchHost): with its shortcut, or (`compact`) just its icon. None without tools to search. */
export function SearchButton({ compact }: Readonly<{ compact?: boolean }>) {
    const open = useContext(SearchOpener);
    if (!open) return null;
    return (
        <button
            type="button"
            className={clsx("flex cursor-pointer items-center gap-2 rounded-md border border-line bg-surface text-sm text-muted hover:text-ink", compact ? "p-1.5" : "w-full px-2.5 py-1.5")}
            title={`Search (${searchShortcut})`}
            aria-label="Search"
            onClick={open}
        >
            <Search className="size-4 shrink-0" />
            {compact ? null : (
                <>
                    <span className="flex-1 text-left">Search</span>
                    <kbd className="rounded border border-line px-1 font-sans text-[11px]">{searchShortcut}</kbd>
                </>
            )}
        </button>
    );
}

function SearchPopup({ tools, onClose }: Readonly<{ tools: readonly ToolMeta[]; onClose: () => void }>) {
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [, navigate] = useLocation();
    const hrefOf = useHitHref();
    const { data, isFetching, q } = useSearchResults(query, tools);
    const hits = data?.hits ?? [];
    const input = useRef<HTMLInputElement>(null);
    useEffect(() => input.current?.focus(), []);
    useEffect(() => setActive(0), [q]);
    const pick = (i: number) => {
        const hit = hits[i];
        if (!hit) return;
        navigate(hrefOf(hit));
        onClose();
    };
    return createPortal(
        // Over everything (windows come forward from 40 up): its shade closes it.
        <div
            className="fixed inset-0 flex items-start justify-center bg-black/30 px-4 pt-[12vh]"
            style={{ zIndex: 100_000 }}
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            onPointerDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="card flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden p-0 shadow-2xl">
                <label className="relative block border-b border-line">
                    <span className="sr-only">Search</span>
                    <Search className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-muted" />
                    <input
                        ref={input}
                        type="search"
                        className="w-full bg-transparent py-3.5 pr-10 pl-12 text-base text-ink outline-none"
                        placeholder={searchPlaceholder(tools)}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                e.preventDefault();
                                onClose();
                            } else if (e.key === "ArrowDown") {
                                e.preventDefault();
                                setActive((i) => Math.min(i + 1, Math.max(hits.length - 1, 0)));
                            } else if (e.key === "ArrowUp") {
                                e.preventDefault();
                                setActive((i) => Math.max(i - 1, 0));
                            } else if (e.key === "Enter") {
                                e.preventDefault();
                                pick(active);
                            }
                        }}
                    />
                    {isFetching ? <Spinner className="absolute top-1/2 right-4 size-4 -translate-y-1/2" /> : null}
                </label>
                <div className="min-h-0 overflow-y-auto">
                    {q ? <SearchGroups data={data} q={q} active={active} onPick={onClose} /> : <p className="m-0 p-4 text-sm text-muted">Type to search. ↑↓ to choose, Enter to open, Esc to close.</p>}
                </div>
            </div>
        </div>,
        document.body,
    );
}
